// ABOUTME: Pure functions for the Reel manifest — schema validation, segment/beat lookup, preload hints.
// ABOUTME: No React, no DOM: everything here is unit-testable and shared by the manifest loader and player.

/**
 * Manifest contract (§14 of the Sequence Pilot review — canonical keys):
 * {
 *   version: 1,
 *   fps: 30,
 *   mediaBase: "/reel/",
 *   boot: "intro",            // optional: chapter auto-played at mount
 *   bootHandoff: "arrival",   // optional: where the boot chapter hands off when it ends.
 *                             // Defaults to `home`. Set it equal to `boot` to make the boot
 *                             // chapter REST at its own neutral end frame instead (the
 *                             // returning-guest path, which boots into the collection sweep).
 *   home: "arrival",          // chapter the `home` verb returns to
 *   chapters: [{
 *     id, title, variant, media, poster?, duration,
 *     segments: [{ type: "move"|"hold"|"exit", start, end, loop?: { start, end },
 *                  detached?: true }],
 *       // move: forward motion (neutral → detail). hold: composed frame the chapter
 *       // rests in — may be a single frozen frame (start === end). exit: the rewind
 *       // (detail → neutral) played ONLY when leaving the chapter, so the next
 *       // chapter's neutral first frame matches and the cut is invisible.
 *       // A hold marked `detached: true` rests on frames OUTSIDE the chapter's own span
 *       // (watch_360 borrows idle's breath — reelSpecTransform): exempt from film-order
 *       // checks, entered by a pose-matched seek. Only loop holds may be detached.
 *     beats:    [{ time, label }],
 *     deixis:   { closer?: <chapterId> },
 *     captions: [{ beat, text }],
 *     allowedTransitions: [<chapterId>]
 *   }]
 * }
 */

const EPSILON = 0.05; // seconds of tolerance for float timecodes

/**
 * Validates a parsed reel manifest. Returns { ok: boolean, errors: string[] }.
 * A bad artifact must fail at load, never mid-demo — so this is exhaustive
 * and every error carries the chapter id it came from.
 */
export function validateManifest(manifest) {
  const errors = [];
  const push = (msg) => errors.push(msg);

  if (!manifest || typeof manifest !== 'object') {
    return { ok: false, errors: ['manifest is not an object'] };
  }
  if (!Array.isArray(manifest.chapters) || manifest.chapters.length === 0) {
    return { ok: false, errors: ['manifest.chapters must be a non-empty array'] };
  }
  if (manifest.playbackRate !== undefined
      && (typeof manifest.playbackRate !== 'number' || manifest.playbackRate <= 0)) {
    push('manifest.playbackRate must be a positive number');
  }

  const ids = new Set();
  for (const ch of manifest.chapters) {
    const tag = `chapter '${ch?.id ?? '?'}'`;

    if (!ch.id || typeof ch.id !== 'string') push(`${tag}: missing id`);
    else if (ids.has(ch.id)) push(`${tag}: duplicate id`);
    else ids.add(ch.id);

    if (!ch.media || typeof ch.media !== 'string') push(`${tag}: missing media filename`);
    if (typeof ch.duration !== 'number' || ch.duration <= 0) push(`${tag}: duration must be > 0`);

    // Segments: non-empty, ordered, contiguous-or-gapped but never overlapping,
    // each within the media duration. Loop points must sit inside their hold.
    if (!Array.isArray(ch.segments) || ch.segments.length === 0) {
      push(`${tag}: segments must be a non-empty array`);
    } else {
      let prevEnd = -Infinity;
      ch.segments.forEach((seg, i) => {
        const sTag = `${tag} segment[${i}]`;
        if (seg.type !== 'move' && seg.type !== 'hold' && seg.type !== 'exit') {
          push(`${sTag}: type must be move|hold|exit`);
        }
        if (typeof seg.start !== 'number' || typeof seg.end !== 'number') {
          push(`${sTag}: start and end must be numbers`);
          return;
        }
        // Holds may be a single frozen frame (start === end); moves and exits must advance.
        if (seg.type !== 'hold' && seg.start >= seg.end) {
          push(`${sTag}: ${seg.type} segment start must be < end`);
          return;
        }
        if (seg.type === 'hold' && seg.start > seg.end) {
          push(`${sTag}: hold segment start must be <= end`);
          return;
        }
        // A `detached` hold rests on frames that live OUTSIDE the chapter's own span (360
        // borrowing idle's breath — reelSpecTransform). It is exempt from the film-order
        // check and does not advance the order cursor; everything else still validates.
        if (seg.detached) {
          if (seg.type !== 'hold' || !seg.loop) push(`${sTag}: only loop holds may be detached`);
        } else {
          if (seg.start < prevEnd - EPSILON) push(`${sTag}: overlaps previous segment`);
          prevEnd = seg.end;
        }
        if (typeof ch.duration === 'number' && seg.end > ch.duration + EPSILON) {
          push(`${sTag}: end ${seg.end} exceeds duration ${ch.duration}`);
        }
        if (seg.loop) {
          if (seg.type !== 'hold') push(`${sTag}: only holds may loop`);
          if (typeof seg.loop.start !== 'number' || typeof seg.loop.end !== 'number'
              || seg.loop.start >= seg.loop.end) {
            push(`${sTag}: loop.start must be < loop.end`);
          } else if (seg.loop.start < seg.start - EPSILON || seg.loop.end > seg.end + EPSILON) {
            push(`${sTag}: loop points must sit inside the segment`);
          }
        }
      });
    }

    // Beats: sorted, within duration, unique labels within the chapter.
    const beatLabels = new Set();
    if (ch.beats !== undefined) {
      if (!Array.isArray(ch.beats)) push(`${tag}: beats must be an array`);
      else {
        let prevT = -Infinity;
        ch.beats.forEach((b, i) => {
          if (typeof b.time !== 'number' || !b.label) push(`${tag} beat[${i}]: needs time + label`);
          else {
            if (b.time < prevT) push(`${tag} beat[${i}]: beats must be sorted by time`);
            prevT = b.time;
            if (typeof ch.duration === 'number' && b.time > ch.duration + EPSILON) {
              push(`${tag} beat[${i}]: time exceeds duration`);
            }
            if (beatLabels.has(b.label)) push(`${tag} beat[${i}]: duplicate label '${b.label}'`);
            beatLabels.add(b.label);
          }
        });
      }
    }

    // Captions must reference declared beats.
    if (ch.captions !== undefined) {
      if (!Array.isArray(ch.captions)) push(`${tag}: captions must be an array`);
      else ch.captions.forEach((c, i) => {
        if (!c.beat || !c.text) push(`${tag} caption[${i}]: needs beat + text`);
        else if (!beatLabels.has(c.beat)) push(`${tag} caption[${i}]: unknown beat '${c.beat}'`);
      });
    }
  }

  // Cross-chapter references: home/boot, deixis targets, allowedTransitions.
  if (!manifest.home || !ids.has(manifest.home)) {
    push(`manifest.home '${manifest.home}' is not a chapter id`);
  }
  if (manifest.boot !== undefined && !ids.has(manifest.boot)) {
    push(`manifest.boot '${manifest.boot}' is not a chapter id`);
  }
  if (manifest.bootHandoff !== undefined && !ids.has(manifest.bootHandoff)) {
    push(`manifest.bootHandoff '${manifest.bootHandoff}' is not a chapter id`);
  }
  for (const ch of manifest.chapters) {
    const tag = `chapter '${ch?.id ?? '?'}'`;
    if (ch.deixis) {
      for (const [verb, target] of Object.entries(ch.deixis)) {
        if (!ids.has(target)) push(`${tag} deixis.${verb}: unknown chapter '${target}'`);
      }
    }
    if (Array.isArray(ch.allowedTransitions)) {
      for (const target of ch.allowedTransitions) {
        if (!ids.has(target)) push(`${tag} allowedTransitions: unknown chapter '${target}'`);
      }
    }
  }

  return { ok: errors.length === 0, errors };
}

/** Index of the segment containing time t; else the last segment whose start <= t; -1 if
 * before the first. Containment is checked per segment rather than by walking film order,
 * so a `detached` hold — whose frames sit outside the chapter's own span — resolves to
 * itself while the guest is breathing in it. For ordered chapters (ascending starts) both
 * rules pick the same segment, including at the shared move/hold/exit boundary frames. */
export function findSegmentIndex(chapter, t) {
  let contained = -1;
  let started = -1;
  for (let i = 0; i < chapter.segments.length; i++) {
    const s = chapter.segments[i];
    if (s.start <= t + EPSILON) {
      started = i;
      if (t <= s.end + EPSILON) contained = i;
    }
  }
  return contained !== -1 ? contained : started;
}

/** Label of the last beat at or before t, or null when no beat has been reached. */
export function resolveBeat(chapter, t) {
  if (!Array.isArray(chapter.beats)) return null;
  let label = null;
  for (const b of chapter.beats) {
    if (b.time <= t + EPSILON) label = b.label;
    else break;
  }
  return label;
}

/** Index of the first hold segment (where revisits and `home` land). Falls back to the last segment. */
export function firstHoldIndex(chapter) {
  const idx = chapter.segments.findIndex((s) => s.type === 'hold');
  return idx === -1 ? chapter.segments.length - 1 : idx;
}

/**
 * The chapter the idle surface should preload next: the first earned direct
 * continuation, else the depth answer to "closer", else home (never itself).
 */
export function likelyNextId(manifest, chapter) {
  if (!chapter) return manifest.home;
  const candidates = [
    ...(Array.isArray(chapter.allowedTransitions) ? chapter.allowedTransitions : []),
    chapter.deixis?.closer,
    manifest.home,
  ];
  return candidates.find((id) => id && id !== chapter.id) ?? null;
}

/** Absolute URL for a chapter's media file. Already-resolved `media` values (an external
 * https:// CDN URL, a `blob:` object URL, or a protocol-relative/root-relative URL) pass
 * through untouched; a bare filename is resolved against `mediaBase`. */
export function mediaUrl(manifest, chapter) {
  const m = chapter.media;
  if (!m) return ''; // validateManifest rejects a missing `media` — this just avoids a throw
  if (/^(https?:|blob:|data:)/i.test(m) || m.startsWith('//') || m.startsWith('/')) return m;
  const base = manifest.mediaBase ?? '/reel/';
  return base + m;
}
